/**
 * The corner plot: where you have walked, as dots.
 *
 * **It is not a map, and the difference is not a technicality.** There are no tiles, no streets,
 * no labels and no third-party request — it draws exactly one thing, which is the output of this
 * device's own GPS over the last little while. The product's rule is that a map is a fine way to
 * choose a point and a terrible way to walk to one; this never claims to be either, it is a
 * record of what already happened. `views/place.ts` remains the only screen with a map on it.
 *
 * It sits permanently in a corner of the stage, which is the one thing about it that has to be
 * earned rather than assumed, because the arrow is the hero and nothing may compete with it. So:
 *
 *   - it is small, dim, and drawn in one colour;
 *   - it says nothing until you have actually walked somewhere, so it is not a decoration that
 *     appears at the start of every session claiming a journey nobody has taken;
 *   - it disappears at the handoff range, where the product has already stopped drawing geometry
 *     because geometry has stopped being true;
 *   - and a tap folds it away for the rest of the session, because a permanent thing on the hero
 *     screen that you cannot dismiss is not a feature, it is an imposition.
 *
 * You are always at the centre. The plot is linear — unlike hold-to-peek, which compresses
 * distance logarithmically to fit a whole group — because a path drawn to a squashed ruler is a
 * path that lies about its own shape, and the shape is the entire point. What that costs is a
 * scale that changes as you walk, so the scale is written on it.
 */

import { formatDistance } from '@beeline/shared';

import { el } from '../lib/dom';
import type { TrackOffset } from '../lib/track';

export interface TrackPlotOptions {
  /** Side of the square plot, in CSS pixels. */
  size?: number;
}

export interface TrackPlotState {
  points: TrackOffset[];
  /** Metres from you to the furthest point — what the plot has to fit. */
  spreadMetres: number;
  /** Heading in degrees, or null for north-up when there is no compass. */
  heading: number | null;
  /** True where the product has stopped pointing: the last fifteen metres. */
  handoff: boolean;
}

export interface TrackPlot {
  element: HTMLElement;
  update: (state: TrackPlotState) => void;
  destroy: () => void;
}

/** Below this there is no track worth drawing, only the beginning of one. */
const MIN_POINTS = 3;

/**
 * The smallest radius the plot will scale to.
 *
 * Without a floor, three fixes taken twenty metres apart would fill the whole plot and read as an
 * expedition. Twenty-five metres is about the distance at which a walk starts having a shape.
 */
const MIN_RADIUS_METRES = 25;

export function createTrackPlot(options: TrackPlotOptions = {}): TrackPlot {
  const size = options.size ?? 84;

  const canvas = el('canvas', { class: 'plot__canvas' }) as HTMLCanvasElement;
  const scaleLabel = el('span', { class: 'plot__scale mono' });
  const north = el('span', { class: 'plot__north', text: 'N' });

  /**
   * The rotating part.
   *
   * The dots are drawn north-up and the whole layer is turned by CSS, so a heading change costs
   * a compositor transform rather than a redraw. The canvas is only ever repainted when the
   * track itself changes, which is at most once a second and usually far less.
   */
  const rotor = el('div', { class: 'plot__rotor' }, canvas, north);

  /**
   * You, at the centre, outside the rotating layer.
   *
   * Without it the plot is a squiggle in a circle and the eye has nothing to read it against —
   * where the path ends is the whole question. It sits outside the rotor because the centre is
   * the one point rotation cannot move, so it should not be dragged through the transition.
   */
  const you = el('span', { class: 'plot__you', 'aria-hidden': 'true' });

  const element = el(
    'button',
    {
      class: 'plot',
      type: 'button',
      hidden: true,
      'data-action': 'track-plot',
      'aria-label': 'Where you have walked. Tap to fold away.',
    },
    rotor,
    you,
    scaleLabel,
  );

  /** Folded away by hand, for the rest of the session. Never persisted. */
  let dismissed = false;
  element.addEventListener('click', () => {
    dismissed = true;
    element.hidden = true;
  });

  /** What the canvas currently shows, so an unchanged track is not repainted. */
  let paintedCount = -1;
  let paintedRadius = -1;

  const paint = (points: TrackOffset[], radiusMetres: number): void => {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const pixels = Math.round(size * dpr);
    if (canvas.width !== pixels) {
      canvas.width = pixels;
      canvas.height = pixels;
    }

    const context = canvas.getContext('2d');
    // happy-dom and a lost GPU context both land here; the plot simply stays blank.
    if (!context) return;

    context.clearRect(0, 0, pixels, pixels);

    const centre = pixels / 2;
    // A hair of margin so a dot at the very edge is not clipped in half.
    const scale = (centre - 3 * dpr) / radiusMetres;
    const newest = points[points.length - 1]?.ageMs ?? 0;
    const oldest = points[0]?.ageMs ?? 0;
    const span = Math.max(1, oldest - newest);

    for (const point of points) {
      const x = centre + point.east * scale;
      const y = centre - point.north * scale;

      // Older is fainter, so the direction of travel is readable without an arrowhead.
      const freshness = 1 - (point.ageMs - newest) / span;
      context.fillStyle = `rgba(255, 180, 84, ${(0.25 + freshness * 0.5).toFixed(3)})`;
      context.beginPath();
      context.arc(x, y, 1.6 * dpr, 0, Math.PI * 2);
      context.fill();
    }
  };

  return {
    element,

    update: (state) => {
      if (dismissed) return;

      /*
       * Hidden at the handoff range on purpose. Below fifteen metres the product stops pointing
       * and says "look around and wave", because the numbers have stopped being true — and a
       * track plot at that range is a drawing of GPS noise. Keeping it up would contradict the
       * one thing the rest of the screen is doing.
       */
      if (state.handoff || state.points.length < MIN_POINTS) {
        element.hidden = true;
        return;
      }

      element.hidden = false;

      const radius = Math.max(MIN_RADIUS_METRES, state.spreadMetres);
      if (state.points.length !== paintedCount || radius !== paintedRadius) {
        paint(state.points, radius);
        paintedCount = state.points.length;
        paintedRadius = radius;
        // Rounded, because a scale bar that reads "37 m" implies a precision the fix has not got.
        scaleLabel.textContent = formatDistance(Math.round(radius / 5) * 5);
      }

      // North-up when there is no compass, exactly like hold-to-peek: a plot that quietly points
      // its "N" at the top of a device that has no idea which way north is would be a lie.
      rotor.style.setProperty(
        '--plot-rotation',
        `${state.heading === null ? 0 : -state.heading}deg`,
      );
      north.style.setProperty('--plot-north', `${state.heading === null ? 0 : state.heading}deg`);
    },

    destroy: () => element.remove(),
  };
}
